/**
 * SouqNexa × Releasit COD Form — single storefront bridge
 *
 * ROOT CAUSE (Playwright-verified on live storefront):
 *
 * Releasit only *paints* multi-card "Buy with Cash on Delivery" portals when:
 *   qt = (pathname === "/") OR (isCollectionFrozen && !ignoreCollectionsV2)
 *
 * isCollectionFrozen = useMemo(() => pathname.includes("/collections/") &&
 *   !pathname.includes("/products/"), [])  // frozen on FIRST read
 *
 * Home (/) and real /collections/* work.
 * /search and /products/*: Releasit still *tags* our .sn-rsi-hook nodes
 * (data-rsi-container) via the isHomePage form scan, but qt stays false, so
 * React never portals BuyNowButton — hooks remain empty forever.
 *
 * We previously tried Object.defineProperty on Location.prototype.pathname.
 * In Chromium, getOwnPropertyDescriptor(Location.prototype, 'pathname') is
 * null — the patch never installed. Hooks looked "present" in HTML audits,
 * but the real Releasit button never appeared on search/PDP.
 *
 * Working approach (proven):
 * 1) history.replaceState to /collections/all BEFORE Releasit freezes
 * 2) Keep isHomePage true, overwriteSelector empty (official multi-form path)
 * 3) After #rsi_buy_now_button mounts, replaceState back to the real URL
 * 4) Frozen isCollection stays true → portals keep working after URL restore
 * 5) Related-products AJAX: ignoreHomeV2 pulse to rescan new forms
 */
(function () {
  var HOOK = '.sn-rsi-hook';
  var CARD_FORM =
    '.sn-purchase-actions__form, .sn-product-card form[action*="/cart/add"], .sn-showcase__actions form[action*="/cart/add"]';

  var realHref =
    window.__SN_RSI_REAL_HREF ||
    window.location.pathname + window.location.search + window.location.hash;
  var realPath = window.__SN_RSI_REAL_PATH || window.location.pathname || '/';

  var isProductPage = realPath.indexOf('/products/') !== -1;
  var isSearchPage = realPath.indexOf('/search') === 0;
  var needsCollectionFacade = isProductPage || isSearchPage;

  var patchTimer = null;
  var rescanTimer = null;
  var restoringHooks = false;
  var urlRestored = !needsCollectionFacade;
  var lastRescanAt = 0;
  var rescanCount = 0;
  var fillWatchStarted = false;
  var ceddRehydrateAttempts = 0;

  function settings() {
    return window._RSI_COD_FORM_SETTINGS || null;
  }

  function isProtectedHook(node) {
    return !!(
      node &&
      node.classList &&
      (node.classList.contains('sn-rsi-hook') || node.hasAttribute('data-sn-rsi-hook'))
    );
  }

  (function protectHooks() {
    var origRemove = Element.prototype.remove;
    Element.prototype.remove = function () {
      if (isProtectedHook(this)) {
        this.removeAttribute('data-rsi-container');
        this.removeAttribute('data-rsi-index');
        return;
      }
      return origRemove.apply(this, arguments);
    };

    var origRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function (child) {
      if (isProtectedHook(child)) {
        child.removeAttribute('data-rsi-container');
        child.removeAttribute('data-rsi-index');
        return child;
      }
      return origRemoveChild.apply(this, arguments);
    };
  })();

  function applyCollectionUrlFacade() {
    if (!needsCollectionFacade) return;
    window.__SN_RSI_REAL_HREF = realHref;
    window.__SN_RSI_REAL_PATH = realPath;
    // After a successful restore, stay on the real URL unless a rescan re-opens the facade.
    if (urlRestored) return;
    window.__SN_RSI_FAKE_COLLECTION = true;
    try {
      window.sessionStorage.setItem('sn_rsi_facade', '1');
      window.sessionStorage.setItem('sn_rsi_real_href', realHref);
    } catch (e) {
      /* ignore */
    }
    if (window.location.pathname.indexOf('/collections/') === 0) return;
    try {
      window.history.replaceState(
        window.history.state,
        '',
        '/collections/all' + (window.__SN_RSI_FACADE_QUERY || '')
      );
    } catch (e) {
      /* ignore */
    }
  }

  function restoreRealUrl() {
    if (urlRestored || !needsCollectionFacade) return;
    urlRestored = true;
    window.__SN_RSI_FAKE_COLLECTION = false;
    try {
      window.sessionStorage.removeItem('sn_rsi_facade');
    } catch (e) {
      /* ignore */
    }
    try {
      window.history.replaceState(window.history.state, '', realHref);
    } catch (e) {
      /* ignore */
    }
    // C-EDD only mounts when location.href contains "/products". Our temporary
    // /collections/all facade can make C-EDD skip painting. Re-run its official
    // script once the real product URL is restored (no custom EDD logic).
    scheduleCeddRehydrate();
  }

  function ceddMountIsEmpty() {
    if (!isProductPage) return false;
    var mount = document.querySelector('.delivery-widget__theme');
    if (!mount) return false;
    if (mount.querySelector('.delivery-widget__container')) return false;
    return (mount.innerHTML || '').trim().length < 40;
  }

  function rehydrateOfficialCedd() {
    if (!ceddMountIsEmpty()) return false;
    if (window.__SN_RSI_FAKE_COLLECTION) return false;
    if ((window.location.href || '').indexOf('/products') === -1) return false;
    if (ceddRehydrateAttempts >= 8) return false;
    if (!window.deliveryED || !window.deliveryED.setting) return false;

    var old = document.querySelector('script[src*="delivery_coder"]');
    if (!old || !old.src) return false;

    ceddRehydrateAttempts += 1;
    try {
      var base = String(old.src).replace(/([?&])r=\d+/g, '$1').replace(/[?&]$/, '');
      var s = document.createElement('script');
      s.src = base + (base.indexOf('?') >= 0 ? '&' : '?') + 'r=' + Date.now();
      s.async = true;
      document.head.appendChild(s);
      return true;
    } catch (e) {
      return false;
    }
  }

  function scheduleCeddRehydrate() {
    if (!isProductPage) return;
    [60, 250, 600, 1200, 2000, 3500, 5000, 7500].forEach(function (ms) {
      window.setTimeout(rehydrateOfficialCedd, ms);
    });
  }

  function countFilledHooks() {
    var n = 0;
    document.querySelectorAll(HOOK).forEach(function (h) {
      if (h.querySelector('#rsi_buy_now_button, button, [data-rsi-loaded], iframe')) n += 1;
    });
    return n;
  }

  function patchSettings() {
    var s = settings();
    if (!s) return false;
    if (!s.productPage) s.productPage = {};
    s.productPage.isEnabled = true;
    s.productPage.overwriteSelector = '';
    s.productPage.ignoreHomeV2 = false;
    s.productPage.ignoreCollectionsV2 = false;
    if (
      needsCollectionFacade ||
      realPath === '/' ||
      realPath.indexOf('/collections/') === 0
    ) {
      s.isHomePage = true;
    }
    try {
      var rsi = window._rsiV2;
      if (rsi && typeof rsi.setState === 'function') {
        rsi.setState({ isHomePage: true });
      }
    } catch (e) {
      /* ignore */
    }
    return true;
  }

  function bumpProducts(tag) {
    try {
      var rsi = window._rsiV2;
      if (!rsi || typeof rsi.setState !== 'function' || typeof rsi.getState !== 'function') {
        return false;
      }
      var st = rsi.getState() || {};
      var products = Array.isArray(st.products) ? st.products.slice() : [];
      rsi.setState({
        products: products.concat([{ id: tag || 'sn-rsi-bump' }]),
        isHomePage: true,
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  function emptyHooksExist() {
    var hooks = document.querySelectorAll(HOOK);
    if (!hooks.length) return false;
    for (var i = 0; i < hooks.length; i++) {
      if (!hooks[i].querySelector('#rsi_buy_now_button, button, [data-rsi-loaded], iframe')) {
        return true;
      }
    }
    return false;
  }

  function ensureHookForForm(form, index) {
    if (!form) return null;
    if (form.closest('.sn-product-card--sold-out')) return null;
    var existing = form.querySelector(HOOK);
    if (existing) return existing;

    var addBtn =
      form.querySelector('[name="add"]') || form.querySelector('button[type="submit"]');
    if (!addBtn || addBtn.disabled) return null;

    var productIdInput = form.querySelector('[name="product-id"]');
    var productId =
      (productIdInput && productIdInput.value) ||
      form.getAttribute('data-product-id') ||
      String(index);

    var slot = document.createElement('div');
    slot.className = 'sn-purchase-actions__cod';
    var hook = document.createElement('div');
    hook.id = 'sn-rsi-dyn-' + productId + '-' + index;
    hook.className = 'sn-rsi-hook rsi-button-hook _rsi-buy-now-button-app-block-hook';
    hook.setAttribute('data-rsi-hook', '');
    hook.setAttribute('data-sn-rsi-hook', '');
    hook.setAttribute('data-product-id', productId);
    slot.appendChild(hook);

    if (addBtn.parentNode) addBtn.parentNode.insertBefore(slot, addBtn.nextSibling);
    else form.appendChild(slot);
    return hook;
  }

  function ensureAllHooks() {
    if (restoringHooks) return;
    restoringHooks = true;
    try {
      document.querySelectorAll(CARD_FORM).forEach(function (form, i) {
        ensureHookForForm(form, i);
      });
      document.querySelectorAll('.product-form__buttons').forEach(function (box, i) {
        var form = box.closest('form[action*="/cart/add"]');
        if (form) ensureHookForForm(form, 'pdp-' + i);
      });
    } finally {
      restoringHooks = false;
    }
  }

  function setRsiProductPageFlags(flags) {
    var s = settings();
    if (s && s.productPage) {
      Object.keys(flags).forEach(function (key) {
        s.productPage[key] = flags[key];
      });
    }
    try {
      var rsi = window._rsiV2;
      if (!rsi || typeof rsi.getState !== 'function' || typeof rsi.setState !== 'function') {
        return false;
      }
      var st = rsi.getState() || {};
      var current = st.productPage || (s && s.productPage) || {};
      var next = {};
      Object.keys(current).forEach(function (key) {
        next[key] = current[key];
      });
      Object.keys(flags).forEach(function (key) {
        next[key] = flags[key];
      });
      rsi.setState({ productPage: next, isHomePage: true });
      return true;
    } catch (e) {
      return false;
    }
  }

  function rescanReleasitContainers() {
    if (!emptyHooksExist()) return;

    var now = Date.now();
    if (now - lastRescanAt < 700) return;
    if (rescanCount > 12) return;
    lastRescanAt = now;
    rescanCount += 1;

    // Brief collection URL pulse for Releasit pathname freeze/rescan only.
    // Always restore the real product URL afterward so C-EDD can paint.
    var shouldPulseFacade = needsCollectionFacade;
    if (shouldPulseFacade) {
      urlRestored = false;
      applyCollectionUrlFacade();
    }

    // Fresh hooks: clear prior scan markers so Releasit re-binds related cards
    document.querySelectorAll(HOOK).forEach(function (hook) {
      if (!hook.querySelector('#rsi_buy_now_button, button, [data-rsi-loaded], iframe')) {
        hook.removeAttribute('data-rsi-container');
        hook.removeAttribute('data-rsi-index');
      }
    });

    // ignoreHomeV2 must be set on Releasit React state (not only window settings),
    // otherwise homePageContainersInitialized never resets after AJAX.
    setRsiProductPageFlags({
      ignoreHomeV2: true,
      ignoreCollectionsV2: false,
      isEnabled: true,
      overwriteSelector: '',
    });
    bumpProducts('sn-clear-' + rescanCount);

    window.setTimeout(function () {
      setRsiProductPageFlags({
        ignoreHomeV2: false,
        ignoreCollectionsV2: false,
        isEnabled: true,
        overwriteSelector: '',
      });
      ensureAllHooks();
      bumpProducts('sn-scan-' + rescanCount);
      if (shouldPulseFacade) {
        window.setTimeout(restoreRealUrl, 250);
      }
    }, 200);
  }

  function scheduleRescan() {
    if (rescanTimer) window.clearTimeout(rescanTimer);
    rescanTimer = window.setTimeout(function () {
      ensureAllHooks();
      rescanReleasitContainers();
    }, 200);
  }

  function watchForFillThenRestore() {
    if (!needsCollectionFacade || fillWatchStarted) return;
    fillWatchStarted = true;
    var tries = 0;
    (function tick() {
      tries += 1;
      var total = document.querySelectorAll(HOOK).length;
      var filled = countFilledHooks();
      var pdpHook = document.querySelector(
        'product-form ' + HOOK + ', .product-form__buttons ' + HOOK + ', .sn-pdp__panel ' + HOOK
      );
      var pdpFilled = !!(
        pdpHook &&
        pdpHook.querySelector('#rsi_buy_now_button, button, [data-rsi-loaded], iframe')
      );

      // Priority: once the main PDP COD button exists, restore the real /products
      // URL so C-EDD can paint. Related-card rescans use a short facade pulse only.
      if (pdpFilled && !urlRestored) {
        restoreRealUrl();
      }

      if (total > 0 && filled < total) {
        scheduleRescan();
      } else if (total > 0 && filled >= total && !urlRestored) {
        restoreRealUrl();
      }

      if (tries < 120) window.setTimeout(tick, 400);
      else if (!urlRestored) restoreRealUrl();
    })();
  }

  function schedulePatch(immediate, withRescan) {
    if (patchTimer) window.clearTimeout(patchTimer);
    var run = function () {
      applyCollectionUrlFacade();
      patchSettings();
      ensureAllHooks();
      watchForFillThenRestore();
      if (withRescan) scheduleRescan();
    };
    if (immediate) run();
    else patchTimer = window.setTimeout(run, 40);
  }

  applyCollectionUrlFacade();

  // Releasit freezes isCollection from pathname on its first React render.
  // After a short window, restore the real /products URL so deferred apps
  // (especially C-EDD, which requires "/products" in location.href) can init
  // correctly. Frozen isCollection stays true, so COD portals keep working.
  if (needsCollectionFacade) {
    window.setTimeout(function () {
      restoreRealUrl();
    }, 700);
    window.setTimeout(function () {
      restoreRealUrl();
    }, 1400);
  }

  var boots = 0;
  function bootLoop() {
    schedulePatch(true, boots === 12 || boots === 24);
    boots += 1;
    if (boots < 60) window.setTimeout(bootLoop, 250);
  }
  bootLoop();

  // Keep retrying official C-EDD paint on product pages until the theme mount fills.
  if (isProductPage) {
    var ceddWatchTries = 0;
    (function ceddWatch() {
      if (!ceddMountIsEmpty()) return;
      if (!urlRestored && needsCollectionFacade) {
        // Still in facade — try restoring if PDP COD already exists.
        var pdpHook = document.querySelector(
          'product-form ' + HOOK + ', .product-form__buttons ' + HOOK + ', .sn-pdp__panel ' + HOOK
        );
        if (
          pdpHook &&
          pdpHook.querySelector('#rsi_buy_now_button, button, [data-rsi-loaded], iframe')
        ) {
          restoreRealUrl();
        }
      } else {
        rehydrateOfficialCedd();
      }
      if (++ceddWatchTries < 25) window.setTimeout(ceddWatch, 800);
    })();
  }

  document.addEventListener('DOMContentLoaded', function () {
    schedulePatch(true, false);
  });

  window.addEventListener('load', function () {
    schedulePatch(true, true);
    window.setTimeout(function () {
      schedulePatch(true, true);
    }, 2000);
  });

  document.addEventListener('sn:recommendations-loaded', function () {
    schedulePatch(true, true);
    window.setTimeout(function () {
      if (countFilledHooks() > 0) restoreRealUrl();
    }, 2500);
  });

  var observer = new MutationObserver(function (mutations) {
    var need = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type !== 'childList' || !m.addedNodes || !m.addedNodes.length) continue;
      for (var j = 0; j < m.addedNodes.length; j++) {
        var n = m.addedNodes[j];
        if (n.nodeType !== 1) continue;
        if (
          (n.matches &&
            (n.matches('.sn-product-card') ||
              n.matches('.sn-purchase-actions') ||
              n.matches('product-recommendations') ||
              n.matches(HOOK))) ||
          (n.querySelector &&
            n.querySelector(
              '.sn-product-card, .sn-purchase-actions, product-recommendations, ' + HOOK
            ))
        ) {
          need = true;
          break;
        }
      }
      if (need) break;
    }
    if (need) schedulePatch(false, true);
  });

  function startObserver() {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver);

  document.addEventListener('DOMContentLoaded', function () {
    if (typeof FacetFiltersForm === 'undefined') return;
    if (FacetFiltersForm.__snCodPatched) return;
    FacetFiltersForm.__snCodPatched = true;
    var original = FacetFiltersForm.renderProductGridContainer;
    if (typeof original !== 'function') return;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      original.call(this, html);
      schedulePatch(true, true);
      window.setTimeout(function () {
        schedulePatch(true, true);
      }, 500);
    };
  });

  window.__SN_RSI_BRIDGE = {
    patch: function () {
      schedulePatch(true, true);
    },
    hooks: function () {
      return document.querySelectorAll(HOOK).length;
    },
    filled: function () {
      return countFilledHooks();
    },
    realPath: function () {
      return realPath;
    },
  };
})();
